/* ==========================================================================
   POS helpers — numbers, line maths, entry numbering, held bills, receipt.

   The arithmetic here mirrors SalesInvoiceModal exactly (gross → line discount
   → tax on the discounted amount → net, then bill-level extra charge, discount
   and round off). A POS bill and a web invoice for the same basket must total
   to the same figure, so this is the one place that maths is written down.
   ========================================================================== */

import dayjs from "dayjs";

/** Number, or the fallback when the value is blank / not a number. */
export const num = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

/** Fixed-decimal string for display. */
export const money = (value, dp = 2) => num(value).toFixed(dp);

/** A blank bill line. */
export const emptyLine = () => ({
  key: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
  product: null,
  name: "",
  code: "",
  barcode: "",
  unit: null,
  unitName: "",
  taxGuid: null,
  taxPercent: 0,
  quantity: 1,
  price: 0,
  mrp: 0,
  foc: 0,
  batch: "",
  discountPercent: 0,
  manual: false,
  notes: "",
});

/**
 * Per-line amounts. Identical to the loop in SalesInvoiceModal: the discount
 * comes off the gross, tax applies to what is left, and the net is the sum.
 */
export const lineTotals = (line) => {
  const quantity = num(line?.quantity);
  const price = num(line?.price);
  const discountPercent = num(line?.discountPercent);
  const taxPercent = num(line?.taxPercent);

  const gross = quantity * price;
  const discountAmount = (gross * discountPercent) / 100;
  const taxable = gross - discountAmount;
  const taxAmount = (taxable * taxPercent) / 100;

  return {
    gross,
    discountAmount,
    taxable,
    taxAmount,
    net: taxable + taxAmount,
  };
};

/**
 * Whole-bill totals.
 *
 * `extraCharge` adds, `billDiscount` and `roundOff` subtract — the same signs
 * the invoice modal applies to addition / subtraction / round, so the two
 * screens never disagree about a total.
 */
export const billTotals = (lines, { extraCharge = 0, billDiscount = 0, roundOff = 0 } = {}) => {
  const totals = (lines || []).reduce(
    (acc, line) => {
      const t = lineTotals(line);
      acc.gross += t.gross;
      acc.lineDiscount += t.discountAmount;
      acc.tax += t.taxAmount;
      acc.net += t.net;
      acc.units += num(line?.quantity);
      return acc;
    },
    { gross: 0, lineDiscount: 0, tax: 0, net: 0, units: 0 }
  );

  const net = totals.net + num(extraCharge) - num(billDiscount) - num(roundOff);

  return {
    ...totals,
    extraCharge: num(extraCharge),
    billDiscount: num(billDiscount),
    roundOff: num(roundOff),
    discountTotal: totals.lineDiscount + num(billDiscount),
    net,
  };
};

/**
 * Only lines that carry a catalogue product and a quantity.
 *
 * Every detail row the API is given must reference a product, so a line without
 * one never reaches the payload — the non-barcode flow attaches a product and
 * overrides only the printed description.
 */
export const isRealLine = (line) => Boolean(line?.product) && num(line?.quantity) > 0;

/**
 * Next number in a prefixed series, e.g. POS1 → POS2.
 *
 * The invoice modal's version assumes every entry number starts with letters
 * and throws on any that does not; this one skips what it cannot parse, so a
 * single odd row in the table can never block a sale.
 */
export const nextEntryNumber = (bills, prefix = "POS") => {
  const highest = (bills || []).reduce((max, bill) => {
    const entry = `${bill?.entrynumber ?? ""}`.trim();
    if (!entry.toUpperCase().startsWith(prefix.toUpperCase())) return max;
    const digits = entry.slice(prefix.length).replace(/\D/g, "");
    if (!digits) return max;
    return Math.max(max, parseInt(digits, 10) || 0);
  }, 0);

  return `${prefix}${highest + 1}`;
};

/** Morning / Afternoon / Night from the wall clock, shown in the top bar. */
export const shiftLabel = (date = new Date()) => {
  const hour = dayjs(date).hour();
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  return "Night";
};

/* ==========================================================================
   Held bills — parked in localStorage, per branch, so a recall survives a
   reload or a crash mid-shift.
   ========================================================================== */

const holdKey = (branchGuid) => `pos.heldBills.${branchGuid || "default"}`;

export const readHeldBills = (branchGuid) => {
  try {
    const raw = window.localStorage.getItem(holdKey(branchGuid));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const writeHeldBills = (branchGuid, bills) => {
  try {
    window.localStorage.setItem(holdKey(branchGuid), JSON.stringify(bills || []));
  } catch {
    /* private mode or a full quota - holding is a convenience, not a promise */
  }
};

/* ==========================================================================
   Receipt
   ========================================================================== */

const escapeHtml = (text) =>
  `${text ?? ""}`.replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[char])
  );

/**
 * 80mm thermal receipt. Kept as a plain HTML string printed through a hidden
 * iframe - the same approach the invoice modal uses - so no print stylesheet
 * from the app can reach it.
 */
export const buildReceiptHtml = ({
  header = {},
  entryNumber,
  invoiceDate,
  customerName,
  cashier,
  salesman,
  lines = [],
  totals = {},
  payments = [],
  paid = 0,
  change = 0,
  notes = "",
  decimals = 2,
  taxLabel = "Tax",
}) => {
  const fmt = (value) => num(value).toFixed(decimals);

  const rows = lines
    .map((line) => {
      const t = lineTotals(line);
      return `
        <tr>
          <td colspan="4" class="name">${escapeHtml(line.name)}</td>
        </tr>
        <tr>
          <td class="qty">${fmt(line.quantity)} x ${fmt(line.price)}</td>
          <td class="disc">${num(line.discountPercent) ? `-${fmt(t.discountAmount)}` : ""}</td>
          <td class="tax">${num(line.taxPercent) ? `${fmt(line.taxPercent)}%` : ""}</td>
          <td class="amt">${fmt(t.net)}</td>
        </tr>`;
    })
    .join("");

  const paymentRows = payments
    .filter((payment) => num(payment.amount) > 0)
    .map(
      (payment) => `
        <tr>
          <td>${escapeHtml(payment.label)}</td>
          <td class="right">${fmt(payment.amount)}</td>
        </tr>`
    )
    .join("");

  const headerLines = [header.header1, header.header2, header.header3, header.header4, header.header5]
    .filter(Boolean)
    .map((line) => `<div class="center">${escapeHtml(line)}</div>`)
    .join("");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(entryNumber)}</title>
    <style>
      @page { size: 80mm auto; margin: 2mm; }
      * { box-sizing: border-box; }
      body {
        width: 76mm;
        margin: 0;
        font-family: "Courier New", monospace;
        font-size: 11px;
        color: #000;
      }
      .center { text-align: center; }
      .right { text-align: right; }
      .bold { font-weight: 700; }
      .store { font-size: 15px; font-weight: 700; }
      .rule { border-top: 1px dashed #000; margin: 5px 0; }
      table { width: 100%; border-collapse: collapse; }
      td { padding: 1px 0; vertical-align: top; }
      .name { font-weight: 700; padding-top: 3px; }
      .qty { width: 42%; }
      .disc { width: 20%; }
      .tax { width: 14%; text-align: right; }
      .amt { width: 24%; text-align: right; font-weight: 700; }
      .total td { font-size: 14px; font-weight: 700; padding-top: 3px; }
      .meta { display: flex; justify-content: space-between; gap: 6px; }
    </style>
  </head>
  <body>
    <div class="center store">${escapeHtml(header.companyName || "")}</div>
    ${headerLines}
    <div class="rule"></div>
    <div class="meta"><span>Bill</span><span class="bold">${escapeHtml(entryNumber)}</span></div>
    <div class="meta"><span>Date</span><span>${escapeHtml(invoiceDate)}</span></div>
    <div class="meta"><span>Customer</span><span>${escapeHtml(customerName || "Walk-in")}</span></div>
    ${cashier ? `<div class="meta"><span>Cashier</span><span>${escapeHtml(cashier)}</span></div>` : ""}
    ${salesman ? `<div class="meta"><span>Salesman</span><span>${escapeHtml(salesman)}</span></div>` : ""}
    <div class="rule"></div>
    <table>${rows}</table>
    <div class="rule"></div>
    <table>
      <tr><td>Subtotal</td><td class="right">${fmt(totals.gross)}</td></tr>
      ${num(totals.lineDiscount) ? `<tr><td>Line discounts</td><td class="right">-${fmt(totals.lineDiscount)}</td></tr>` : ""}
      ${num(totals.billDiscount) ? `<tr><td>Bill discount</td><td class="right">-${fmt(totals.billDiscount)}</td></tr>` : ""}
      ${num(totals.extraCharge) ? `<tr><td>Extra charge</td><td class="right">${fmt(totals.extraCharge)}</td></tr>` : ""}
      ${num(totals.tax) ? `<tr><td>${escapeHtml(taxLabel)}</td><td class="right">${fmt(totals.tax)}</td></tr>` : ""}
      ${num(totals.roundOff) ? `<tr><td>Round off</td><td class="right">${fmt(-num(totals.roundOff))}</td></tr>` : ""}
      <tr class="total"><td>TOTAL</td><td class="right">${fmt(totals.net)}</td></tr>
    </table>
    <div class="rule"></div>
    <table>
      ${paymentRows}
      <tr><td class="bold">Paid</td><td class="right bold">${fmt(paid)}</td></tr>
      <tr><td class="bold">Change</td><td class="right bold">${fmt(change)}</td></tr>
    </table>
    ${notes ? `<div class="rule"></div><div>${escapeHtml(notes)}</div>` : ""}
    <div class="rule"></div>
    ${header.footerText1 ? `<div class="center bold">${escapeHtml(header.footerText1)}</div>` : ""}
    ${header.footerText2 ? `<div class="center">${escapeHtml(header.footerText2)}</div>` : ""}
    <div class="center">Items: ${lines.length} &nbsp; Units: ${fmt(totals.units)}</div>
    <br />
  </body>
</html>`;
};

/** Prints an HTML string through a throw-away iframe. */
export const printHtml = (html) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow.focus();
  setTimeout(() => {
    iframe.contentWindow.print();
    // Some browsers never fire onafterprint; the timer is the safety net.
    iframe.contentWindow.onafterprint = () => iframe.remove();
    setTimeout(() => iframe.isConnected && iframe.remove(), 60000);
  }, 350);
};
